// tabs/NotesTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Center,
  Loader,
  Stack,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  Checkbox,
  Textarea,
  Button,
  ScrollArea,
  Box,
} from '@mantine/core';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useCareCaseService, useUserService, CareCaseNote } from '@actschurch/ccms-sdk';
import { IconRefresh, IconSend } from '@tabler/icons-react';

interface NotesTabProps {
  caseID: number;
  active: boolean;
  currentUserId?: string;
  /** Pass true if the viewer is a leader — hides admin-only notes and hides the toggle */
  isLeaderView?: boolean;
}

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  message: string;
  timestamp: string;
  isEdited: boolean;
  hideFromLeader: boolean;
  hideFromMembers: boolean;
  createdDateTime: number | string;
  entryDateTime: number | string;
}

/** Which audiences a note is withheld from. Both flags can be set at once. */
function hiddenAudiences(note: { hideFromLeader: boolean; hideFromMembers: boolean }): string[] {
  return [
    note.hideFromLeader ? 'leader' : null,
    note.hideFromMembers ? 'counselees' : null,
  ].filter((v): v is string => v !== null);
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

function decodeNote(value: string): string {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return value ?? '';
  }
}

function encodeNote(value: string): string {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
}

export default function NotesTab({ caseID, active, currentUserId, isLeaderView }: NotesTabProps) {
  const careCaseService = useCareCaseService();
  const userService = useUserService();

  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [draft, setDraft] = useState('');
  const [hideFromLeader, setHideFromLeader] = useState(false);
  /**
   * ⚠️ `hideFromMembers` (SDK 0.0.54) is a **separate** audience from `hideFromLeader`.
   *
   * Until it existed, "hidden from counselees" rested on an unenforced assumption that case notes are
   * never surfaced to members anywhere. A declined case's reason is stored as a case note, so that
   * assumption was holding privacy on its own.
   */
  const [hideFromMembers, setHideFromMembers] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const nameCache = useRef<Map<string, string>>(new Map());

const resolveName = useCallback(async (userId: string): Promise<string> => {
  if (!userId) return 'Staff';
  if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
  try {
    const res: any = await userService.getById(userId);
    const user = res?.data ?? res;
    if (user) {
      const name =
        [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
        user.displayName?.trim() ||
        user.email ||
        userId;
      nameCache.current.set(userId, name);
      return name;
    }
  } catch {
    // fall through
  }
  nameCache.current.set(userId, userId);
  return userId;
}, [userService]);
  
  const noteToItem = useCallback(async (note: CareCaseNote): Promise<NoteItem> => {
  const authorId = note.userId ?? note.adminUserId ?? '';
  let authorName = 'Unknown';
  if (note.userId) {
    authorName = await resolveName(note.userId);
  } else if (note.adminUserId) {
    authorName = `${await resolveName(note.adminUserId)} (Admin)`;
  }
  return {
    id: String(note.noteID),
    noteID: note.noteID,
    authorId,
    authorName,
    hideFromLeader: note.hideFromLeader,
    hideFromMembers: note.hideFromMembers,
    message: decodeNote(note.note ?? ''),
    timestamp: sdkDateToISO(note.entryDateTime),
    isEdited: !!note.modifiedDateTime && note.modifiedDateTime !== note.createdDateTime,
    createdDateTime: note.createdDateTime,
    entryDateTime: note.entryDateTime,
  };
}, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const raw: CareCaseNote[] = await careCaseService.notes(caseID);
      const visible = isLeaderView ? (raw ?? []).filter((n) => !n.hideFromLeader) : raw ?? [];
      const sorted = [...visible].sort((a, b) => a.entryDateTime - b.entryDateTime);
      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching notes:', e);
      toast.error('Error', 'Could not load notes.');
    } finally {
      setLoading(false);
    }
  }, [caseID, careCaseService, noteToItem, isLeaderView]);

  useEffect(() => { if (active) fetchNotes(); }, [active, fetchNotes]);

  const [currentUserName, setCurrentUserName] = useState('You');
  useEffect(() => {
    if (!currentUserId) return;
    resolveName(currentUserId).then(setCurrentUserName);
  }, [currentUserId, resolveName]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      await careCaseService.addNote(caseID, {
        note: encodeNote(content),
        hideFromLeader,
        hideFromMembers,
      } as any);
      setDraft('');
      setHideFromLeader(false);
      setHideFromMembers(false);
      await fetchNotes();
    } catch (e) {
      console.error('Error adding note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;
    try {
      await careCaseService.updateNote(caseID, item.noteID, { note: encodeNote(content) } as any);
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDelete = async (item: NoteItem) => {
    try {
      await careCaseService.deleteNote(caseID, item.noteID);
      await fetchNotes();
      toast.warning('Note Deleted', 'Note removed.');
    } catch (e) {
      console.error('Error deleting note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading notes…</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="right">
        <Tooltip label="Refresh notes" withArrow>
          <ActionIcon variant="subtle" onClick={fetchNotes} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* `mih` as well as `mah`: without a floor the thread collapses to the height of its
          content, so an empty or one-note case left the composer jammed under the toolbar. */}
      <ScrollArea.Autosize mah={480} mih={320} type="auto">
        <Stack gap={4} py="xs" style={{ minHeight: 320 }}>
          {notes.length === 0 && (
            <Center py="lg" style={{ flex: 1 }}>
              <Text size="sm" c="dimmed">No notes yet.</Text>
            </Center>
          )}
          {notes.map((item) => {
            const isOwn = !!currentUserId && item.authorId === currentUserId;
            if (editingId === item.id) {
              return (
                <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0}>
                  <Textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.currentTarget.value)}
                    autosize
                    minRows={2}
                  />
                  <Group gap="xs" justify="flex-end" mt={4}>
                    <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                    <Button size="xs" onClick={() => handleSaveEdit(item)}>
                      Save
                    </Button>
                  </Group>
                </Box>
              );
            }
            return (
              <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                {(item.hideFromLeader || item.hideFromMembers) && (
                  <Text
                    size="xs"
                    c="dimmed"
                    ta={isOwn ? 'right' : 'left'}
                    mb={2}
                    px={4}
                  >
    {/* Both can be set, so name each audience rather than saying just "hidden". */}
                    {`(hidden from ${hiddenAudiences(item).join(' and ')})`}
                  </Text>
                )}
                <ChatBubble
                  message={item.message}
                  sender={{ name: item.authorName }}
                  timestamp={item.timestamp}
                  isOwn={isOwn}
                  isEdited={item.isEdited}
                  onEdit={isOwn ? () => startEdit(item) : undefined}
                  onDelete={isOwn ? () => handleDelete(item) : undefined}
                  onCopy={() => {
                    navigator.clipboard.writeText(item.message);
                    toast.success('Copied', 'Note copied to clipboard.');
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </ScrollArea.Autosize>

      <Stack gap="xs">
        <Textarea
          placeholder="Write a note…"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
        />
        <Group justify="space-between">
          <Group gap="md">
            {!isLeaderView && (
              <Checkbox
                label="Hide from leader"
                checked={hideFromLeader}
                onChange={(e) => setHideFromLeader(e.currentTarget.checked)}
              />
            )}
            <Checkbox
              label="Hide from counselees"
              checked={hideFromMembers}
              onChange={(e) => setHideFromMembers(e.currentTarget.checked)}
            />
          </Group>
          <Button
            leftSection={<IconSend size={14} />}
            onClick={handlePost}
            loading={posting}
            disabled={!draft.trim()}
            ml="auto"
          >
            Post
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
}